// BRIEF 103 §2 — THE ENROLMENT INVITATION'S TWO SETTINGS, GIVEN THE SCREEN THEY
// NEVER HAD.
//
// §2a: "one panel, two fields: the window and the witness. They are two halves of
//  one decision — how long the secret lives, and what besides the secret proves
//  who turned up — and they were split only because the screen was never built."
//
// The route has accepted both since 83 §3(4) (`server/src/routes/settings.js:48`,
// admin-only) and nothing in `src/` ever wrote either, so a firm's policy was
// whatever the defaults were unless somebody edited the database by hand.
//
// ── §2b — TWO OF THE FOUR WITNESSES ARE NOT IMPLEMENTED, AND THE FIELD SAYS SO ─
//
// "A settings field that offers an unimplemented option is worse than no field.
//  Decide and say which: offer only what is honoured today, or offer all four
//  with the unbuilt ones plainly marked. Do not offer them silently."
//
// **ALL FOUR ARE OFFERED AND THE TWO UNBUILT ONES ARE MARKED IN THE OPTION LABEL
// ITSELF**, not only in prose beside the field. Three reasons, in order:
//
//   1. `witnessFrom` accepts all four and `refuseWitness` refuses everything
//      else, so the server ALREADY stores any of them. A field offering two
//      would be a screen that cannot display a value the database legitimately
//      holds — a firm that set `channel` by hand would open this panel and see
//      `in_app`, which is a screen lying about the firm's own policy.
//   2. The marking travels with the choice. A sentence under the field is read
//      once; the label is read at the moment of choosing, which is the moment
//      that matters.
//   3. Hiding them would make the setting look complete. `phrase` and `channel`
//      are declared, bounds-checked, refused-by-name and asserted in the
//      selftest, and honoured NOWHERE — that is a debt, and a screen that
//      conceals it is how the debt stops being paid.
//
// Only `approval` is read anywhere today (`AuthIncidentPanel.tsx:408`), and
// `in_app` needs no enforcement because it is the absence of a delivery channel.
//
// ── WHAT THE WINDOW FIELD SHOWS A FIRM THAT NEVER LEFT DAYS ─────────────────
//
// §1a chose a NEW key over redefining `authInvitationDays`, with the old row
// READ and converted rather than migrated. So a firm that set three days sees
// 72 here, with a line saying where that number came from — not an empty field,
// and not a number it cannot account for.
import { useState, useEffect } from "react";
import { useTranslation } from "../i18n";
import { FS } from "../lib/tokens";
import { getInvitationPolicy, saveInvitationPolicy } from "../api";
import type { InvitationPolicy } from "../api";
import MenuSelect from "./MenuSelect";
import { withCount } from "../lib/pluralVars"; // 209 §7 — a sentence's number, read once, as its placeholder and as `count` // 154B §2

type Toast = (message: string) => void;
type ApiFailure = { message?: string };

/** The server's four, in the server's order (`enrolmentInvitation.js:78`). */
const WITNESSES = ["in_app", "channel", "approval", "phrase"] as const;

/** Which of them this build actually acts on. `channel` and `phrase` do not. */
const HONOURED = new Set<string>(["in_app", "approval"]);

const MIN_HOURS = 1;
const MAX_HOURS = 720;

export default function InvitationPolicyPanel({
  isAdmin,
  toast,
}: {
  isAdmin?: boolean;
  toast?: Toast;
}) {
  const { t } = useTranslation();
  const [cfg, setCfg] = useState<InvitationPolicy | null>(null);
  const [hours, setHours] = useState("");
  const [witness, setWitness] = useState("in_app");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let live = true;
    getInvitationPolicy()
      .then((p) => {
        if (!live) return;
        setCfg(p);
        setHours(String(p.hours));
        setWitness(p.witness);
      })
      .catch(() => {
        /* A non-admin never gets here; anything else leaves the panel unset and
           the sentence below says so rather than showing a made-up number. */
        if (live) setErr(t("invite.policy.load_failed", { fallback: "Could not read this firm's invitation policy." }));
      });
    return () => {
      live = false;
    };
  }, [t]);

  if (!isAdmin) return null;

  /* The SAME bounds the server refuses by name (`refuseHours`), stated here so a
     firm finds out while typing rather than on save — §1d's discipline, at the
     other end of the wire. This is not the authority: the route still refuses,
     and a client that got out of step would be corrected by it. */
  const n = Number(hours);
  const bad =
    hours.trim() === "" ||
    !Number.isFinite(n) ||
    Math.floor(n) !== n ||
    n < MIN_HOURS ||
    n > MAX_HOURS;

  const save = async () => {
    if (bad) return;
    setBusy(true);
    setErr("");
    try {
      await saveInvitationPolicy(n, witness);
      const fresh = await getInvitationPolicy();
      setCfg(fresh);
      setHours(String(fresh.hours));
      setWitness(fresh.witness);
      toast && toast(t("invite.policy.saved", { fallback: "Invitation policy saved." }));
    } catch (e) {
      setErr(
        String((e as ApiFailure)?.message || "").trim() ||
          t("invite.policy.save_failed", { fallback: "That could not be saved." }),
      );
    } finally {
      setBusy(false);
    }
  };

  const witnessMeaning = (w: string) =>
    w === "in_app"
      ? t("invite.witness.in_app.means", {
          fallback:
            "The person is already signed in as themselves somewhere, so nothing has to be delivered and there is no channel to intercept.",
        })
      : w === "channel"
        ? t("invite.witness.channel.means", {
            fallback:
              "You hand the link over by a second route — in person, by phone — so holding one message is not enough.",
          })
        : w === "approval"
          ? t("invite.witness.approval.means", {
              fallback:
                "The person cannot finish until an administrator approves what actually turned up: the address it came from, the country, the device.",
            })
          : t("invite.witness.phrase.means", {
              fallback:
                "A phrase is shown at both ends and confirmed out loud, so whoever accepts has to be the person you spoke to.",
            });

  const witnessLabel = (w: string) => {
    const name =
      w === "in_app"
        ? t("invite.witness.in_app", { fallback: "Already signed in elsewhere" })
        : w === "channel"
          ? t("invite.witness.channel", { fallback: "Handed over by another route" })
          : w === "approval"
            ? t("invite.witness.approval", { fallback: "An administrator approves the acceptance" })
            : t("invite.witness.phrase", { fallback: "A matching phrase, confirmed out loud" });
    return HONOURED.has(w)
      ? name
      : `${name} — ${t("invite.witness.not_enforced_suffix", { fallback: "declared, not enforced yet" })}`;
  };

  return (
    <div>
      {/* ── BRIEF 105 §1b-iv — THE SENTENCE THAT BECAME FALSE ────────────────
          It read: "This app never sends it: it is shown to you once, on
          screen, and you pass it on however you choose." Every word of that
          was true when 96 §3b wrote it and it stopped describing this screen
          the moment §1b-i gave the incident panel a button that opens a draft
          addressed to the collaborator, in every language the firm has.
          DRAFTING IS NOT SENDING and the app still transmits nothing — the
          draft opens in the reader's own mail program and he sends it, changes
          it or throws it away. But "you pass it on however you choose" now
          reads as a promise that the app offers no channel, and it offers one.
          A security notice that is subtly false is worse than none, so the
          sentence says what is true and then says what to do about it: the
          out-of-band handover is still the better one, and the mitigation for a
          link that goes through a mailbox is THE WINDOW — the field directly
          below this paragraph — rather than a refusal to draft.
          Ruled 2026-08-19; see RULINGS.md.

          ── AND IT IS A NEW KEY, WHICH IS THE HALF THAT IS EASY TO GET WRONG ─
          `seed_deploy_translations.js` is `createMany({ skipDuplicates: true })`
          BY DESIGN — the database is canonical for any value an administrator
          has corrected through the `/api/i18n` xlsx round-trip, so a row that
          exists is never overwritten. Editing `invite.policy.lede`'s three rows
          in the seed file would therefore have changed NOTHING on any database
          that already had them: the file, the fallback and the PR would all
          have said the sentence was fixed while every screen kept the false
          one. `invite.policy.lede_drafts` is net-new, so it lands everywhere on
          the next seed. Same move 103 §1a made for `authInvitationHours`, and
          for the same underlying reason: when what a stored value MEANS
          changes, the old row cannot be trusted to carry the new meaning.
          The old rows stay in the file, verbatim, with nothing reading them. */}
      <p style={{ fontSize: FS.s11_5, color: "var(--ts)", margin: "6px 0 10px", lineHeight: 1.6 }}>
        {t("invite.policy.lede_drafts", {
          fallback:
            "An enrolment invitation is a one-time link that both proves who somebody is and lets them set up their sign-in. This app still sends nothing — it shows you the link once, and offers to open a draft in your own email program, which is yours to send, change or discard. Handing it over in person or by phone remains better; if you put it in a mailbox, set the window below low.",
        })}
      </p>

      {err && (
        <div style={{ fontSize: FS.s11, color: "var(--dg, #e5484d)", margin: "0 0 8px" }}>{err}</div>
      )}

      {/* ── the window ───────────────────────────────────────────────────── */}
      <div style={{ margin: "0 0 14px" }}>
        <label
          htmlFor="inviteWindowHours"
          style={{ display: "block", fontSize: FS.s11, fontWeight: 600, color: "var(--tp)", marginBottom: 4 }}
        >
          {t("invite.policy.window_label", { fallback: "How long a link stays usable" })}
        </label>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <input
            id="inviteWindowHours"
            className="field-input"
            type="number"
            min={MIN_HOURS}
            max={MAX_HOURS}
            step={1}
            value={hours}
            disabled={busy || !cfg}
            onChange={(e) => setHours(e.target.value)}
            style={{ width: 96, margin: 0 }}
          />
          <span style={{ fontSize: FS.s11, color: "var(--ts)" }}>
            {t("invite.policy.hours_unit", { fallback: "hours" })}
          </span>
        </div>
        <p style={{ fontSize: FS.s10_5, color: "var(--tm)", margin: "4px 0 0", lineHeight: 1.5 }}>
          {t("invite.policy.window_help", {
            min: MIN_HOURS,
            max: MAX_HOURS,
            fallback:
              "Between 1 and 720 hours — thirty days at the outside. The bounds are there to catch a typo, not to judge your firm. The default is 24 hours: long enough to pass a link on when the person is next at their desk, short enough that one left in a chat thread is dead by morning.",
          })}
        </p>
        {bad && hours.trim() !== "" && (
          <p style={{ fontSize: FS.s10_5, color: "var(--dg, #e5484d)", margin: "4px 0 0" }}>
            {t("invite.policy.window_bad", {
              min: MIN_HOURS,
              max: MAX_HOURS,
              fallback: "That has to be a whole number of hours between 1 and 720.",
            })}
          </p>
        )}
        {cfg?.fromLegacyDays && (
          /* §1a — the carry-over, said out loud. This firm never wrote the hours
             key; the number above is its old days setting converted, and it stays
             in force until this panel is saved. */
          <p style={{ fontSize: FS.s10_5, color: "var(--tm)", margin: "4px 0 0", lineHeight: 1.5 }}>
            {t("invite.policy.from_legacy_days", withCount(Math.round(cfg.hours / 24), "d", { n: cfg.hours,
              fallback: `Carried over from the setting your firm had in days (${Math.round(
                cfg.hours / 24,
              )} days = ${cfg.hours} hours). Saving here replaces it.`,
            }))}
          </p>
        )}
      </div>

      {/* ── the witness ──────────────────────────────────────────────────── */}
      <div style={{ margin: "0 0 12px" }}>
        <label
          htmlFor="inviteWitness"
          style={{ display: "block", fontSize: FS.s11, fontWeight: 600, color: "var(--tp)", marginBottom: 4 }}
        >
          {t("invite.policy.witness_label", {
            fallback: "What proves who turned up, besides the link itself",
          })}
        </label>
        {/* 154B §2 — the `id` survives the conversion because a <button> is a
            labelable element, so the <label htmlFor="inviteWitness"> above
            still names this control. */}
        <MenuSelect
          id="inviteWitness"
          className="field-input"
          value={witness}
          disabled={busy || !cfg}
          onChange={(v) => setWitness(v)}
          style={{ margin: 0, maxWidth: 420 }}
          options={WITNESSES.map((w) => [w, witnessLabel(w)] as const)}
        />
        <p style={{ fontSize: FS.s10_5, color: "var(--tm)", margin: "4px 0 0", lineHeight: 1.5 }}>
          {witnessMeaning(witness)}
        </p>
        {!HONOURED.has(witness) && (
          /* §2b — never offered silently. The option is selectable because the
             server stores it, and this says exactly what selecting it does today:
             it is recorded and nothing acts on it. */
          <p style={{ fontSize: FS.s10_5, color: "var(--am)", margin: "6px 0 0", lineHeight: 1.5 }}>
            {t("invite.policy.witness_not_enforced", {
              fallback:
                "This one is not built yet. Choosing it records your firm's intention and changes nothing the app does — acceptance is not held back and nothing is checked. Only “An administrator approves the acceptance” is enforced today.",
            })}
          </p>
        )}
      </div>

      <button
        type="button"
        className="btn btn-primary"
        disabled={busy || bad || !cfg}
        onClick={save}
      >
        {busy
          ? t("common.saving", { fallback: "Saving…" })
          : t("common.save", { fallback: "Save" })}
      </button>
    </div>
  );
}
